import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../Auth/AuthContext";
import BASE_URL from "../../../config/config";
import DateDisplay from "../../common/DateDisplay";

const CourseList = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csrfToken, setCsrfToken] = useState("");
  const { user: currentUser } = useAuth();

  // پارامترهای صفحه‌بندی
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // پارامترهای جستجو
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searching, setSearching] = useState(false);

  const searchInputRef = useRef(null);
  const wasFocused = useRef(false);

  // پارامترهای مرتب‌سازی
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // دریافت توکن CSRF
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${BASE_URL}/csrf-token`, {
          credentials: "include",
        });
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } catch (error) {
        setError("خطا در دریافت توکن امنیتی");
      }
    };

    fetchCsrfToken();
  }, []);

  // دریافت لیست دوره‌ها
  useEffect(() => {
    if (csrfToken) {
      fetchCourses();
    }
  }, [csrfToken, currentPage, searchQuery, sortField, sortDirection]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      let url = `${BASE_URL}/courses?page=${currentPage}&limit=${itemsPerPage}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      url += `&sortField=${sortField}&sortDirection=${sortDirection}`;

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("خطا در دریافت لیست دوره‌ها");
      }

      const data = await response.json();
      setCourses(data.data.courses);
      setTotalItems(data.data.pagination.totalCourses);
      setTotalPages(data.data.pagination.totalPages);
      setLoading(false);
      setSearching(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setSearching(false);
    }
  };

  // حذف دوره
  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("آیا از حذف این دوره اطمینان دارید؟")) return;

    try {
      const response = await fetch(`${BASE_URL}/courses/${courseId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("خطا در حذف دوره");
      }

      alert("دوره با موفقیت حذف شد");
      fetchCourses();
    } catch (err) {
      alert(err.message);
    }
  };

  // تغییر ترتیب مرتب‌سازی
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // مدیریت جستجو
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
    setSearching(true);
  };

  // پاک کردن جستجو
  const clearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // محاسبه قیمت نهایی با تخفیف
  const calculateFinalPrice = (price, discount) => {
    const originalPrice = parseFloat(price) || 0;
    const discountPercent = parseFloat(discount) || 0;
    return originalPrice - (originalPrice * discountPercent) / 100;
  };

  return (
    <div className="course-list-container">
      <div className="course-list-header">
        <h2>لیست دوره‌ها</h2>
        <button
          onClick={() => (window.location.href = "/admin/courses/add")}
          className="add-button"
        >
          افزودن دوره جدید
        </button>
      </div>

      {/* باکس جستجو */}
      <div className="search-box">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            ref={searchInputRef}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="جستجو در دوره‌ها..."
          />
          <button type="submit">جستجو</button>
          {searchQuery && (
            <button type="button" onClick={clearSearch}>
              پاک کردن
            </button>
          )}
        </form>
      </div>

      {loading ? (
        <div>در حال بارگذاری...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : courses.length === 0 ? (
        <div className="no-records">هیچ دوره‌ای یافت نشد</div>
      ) : (
        <div className="table-responsive">
          <table className="courses-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("id")}>شناسه</th>
                <th>تصویر</th>
                <th onClick={() => handleSort("title")}>عنوان</th>
                <th>قیمت</th>
                <th>تخفیف</th> {/* اضافه شد */}
                <th>وضعیت</th>
                <th onClick={() => handleSort("created_at")}>تاریخ ایجاد</th>
                <th>تعداد دانشجویان</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course.id}>
                  <td>{course.id}</td>
                  <td>
                    <img
                      src={course.cover_pic || "/images/course-default.jpg"}
                      alt={course.title}
                      className="course-thumbnail"
                    />
                  </td>
                  <td>{course.title}</td>
                  <td>
                    {course.is_free ? (
                      <span className="free-badge">رایگان</span>
                    ) : (
                      <div className="price-cell">
                        <div
                          className={
                            parseFloat(course.discount) > 0
                              ? "original-price strikethrough"
                              : "original-price"
                          }
                        >
                          {parseFloat(course.price).toLocaleString()} تومان
                        </div>
                        {parseFloat(course.discount) > 0 && (
                          <div className="final-price-small">
                            {calculateFinalPrice(
                              course.price,
                              course.discount
                            ).toLocaleString()}{" "}
                            تومان
                          </div>
                        )}
                      </div>
                    )}
                  </td>

                  <td>
                    {course.is_free ? (
                      <span className="no-discount">-</span>
                    ) : parseFloat(course.discount) > 0 ? (
                      <div className="discount-cell">
                        <span className="discount-badge">
                          {course.discount}%
                        </span>
                      </div>
                    ) : (
                      <span className="no-discount">بدون تخفیف</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        course.is_available ? "active" : "inactive"
                      }`}
                    >
                      {course.is_available ? "فعال" : "غیرفعال"}
                    </span>
                  </td>
                  <td>
                    <DateDisplay isoDate={course.created_at} />
                  </td>
                  <td>{course.enrolled_students}</td>
                  <td className="actions">
                    <button
                      onClick={() =>
                        (window.location.href = `/admin/courses/buyers/${course.id}`)
                      }
                      className="buyers-btn"
                      title="لیست خریداران"
                    >
                      <i className="fas fa-users"></i>
                    </button>
                    <button
                      onClick={() =>
                        (window.location.href = `/admin/courses/edit/${course.id}`)
                      }
                      className="edit-btn"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteCourse(course.id)}
                      className="delete-btn"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* صفحه‌بندی */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            قبلی
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={currentPage === i + 1 ? "active" : ""}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
          >
            بعدی
          </button>
        </div>
      )}
    </div>
  );
};

export default CourseList;
